import * as React from 'react'

import SimpleAlert from '@components/modal/SimpleAlert'

import useReducer from './useReducer'
import * as SyncStore from './sync/sync'
import * as EDLStore from './edl/edl'
import * as IncidentStore from './incident/incident'

import useConnectivity from '@utils/useConnectivity'
import useI18n from '@i18n/useI18n'

import api from '@api/api'

import * as uuid from '@utils/uuid'
import Logger from '@utils/logger'

let synchronizing = false

/**
 * Synchro for incident item
 * @param toSync incident to sync
 * @returns id of the synchonized incident or undefined if failed
 */

enum FailType {
  IMAGE = 'IMAGE',
  COMMENT = 'COMMENT',
}

const synchronizeIncident = (toSync: SyncIncident, onFail?: (type: FailType) => void) => {
  return api.incident
    .create(toSync.incident)
    .then((res) => {
      return (toSync.comment.commentaire ? api.incident.comment(res.id, toSync.comment, true) : Promise.resolve())
        .then(() =>
          toSync.images.length === 0
            ? Promise.resolve([])
            : Promise.all(toSync.images.map((im) => api.incident.addImage(res.id, im))).catch(() => {
                if (onFail) {
                  onFail(FailType.IMAGE)
                }
              })
        )
        .then(() => SyncStore.actions.removeIncident(toSync.id)) // completed, we remove
        .then(() => res.id)
        .catch(() => {
          if (onFail) {
            onFail(FailType.COMMENT)
          }

          return res.id
        })
    })
    .catch(() => undefined) // return undefined if upload failed
}

/**
 * Convert form to syncIncident
 * @params form to convert
 * @returns request to sync
 */
const createRequest = (form: IncidentForm) => {
  const body: CreationIncidentRequest = {
    commandeAssociee:
      form.numeroCommande || form.libelleFamille
        ? {
            enseigne: form.enseigneCommande,
            allee: form.alleeCommande,
            espace: form.espaceCommande,
            isOrganisateur: !!form.isCommandeOrganisateur,
            numeroCommande: form.numeroCommande,
            numeroLigne: form.numeroLigne,
            referenceArticle: form.referenceArticleCommande,
            stand: form.standCommande,
            libelleFamille: form.libelleFamille,
            libelleSousFamille: form.libelleSousFamille,
            libelleArticle: form.libelleArticle,
          }
        : undefined,
    contactAssocie: form.emailContact
      ? {
          nom: form.nomContact,
          prenom: form.prenomContact,
          email: form.emailContact,
          telephone: form.telephoneContact,
        }
      : undefined,
    famillePrestationId: form.prestation || form.familles[form.familles.length - 1],
    siteId: form.siteId,
    isPrioritaire: !!form.isPrioritaire,
    localisationId: form.subsubLocation || form.subLocation || form.location,
    manifestationId: form.manifestationId,
    quantite: form.quantite,
    societePrestataireId: form.societePrestataireId === 'none' ? undefined : form.societePrestataireId,
    plan: form.plan,
    remonteDansEtatDesLieux: !!form.edlId || !!form.edl, // from edl or checked in detail
  }

  const comment: AddCommentIncidentRequest = {
    commentaire: form.commentaire,
    commentaireType: 'COMMENTAIRE' as CommentaireType,
  }

  const toSync: SyncIncident = {
    id: uuid.createUUID(),
    comment,
    incident: body,
    images: form.photos || [],
  }

  return toSync
}

/**
 * Convert form to syncIncident and add it to store for synchronization
 */
const addIncident = (form: IncidentForm) => {
  const request = createRequest(form)
  SyncStore.actions.addIncident(request)

  return request
}

/**
 * Synchro hook to upload incidents with offline handler
 * @param onItemSynchronized callback to get ids of items synchronized
 * @returns [status of sync / local incidents / utils for store manipulation]
 */
const useSyncStore = () => {
  const [online] = useConnectivity()
  const [status, setStatus] = React.useState<'PENDING' | 'UPLOADING' | 'UPLOADED'>('UPLOADED')

  const incidents = useReducer(SyncStore.store, (s) => s.incidents)
  const form = useReducer(EDLStore.store, (s) => s.form)
  const i18n = useI18n()

  const onFail = (type: FailType, onFailed?: () => void) => {
    SimpleAlert.open({
      title: i18n.t(`screens.incident.errors.${type}.title`),
      description: i18n.t(`screens.incident.errors.${type}.description`),
      cancel: false,
      buttons: [
        {
          label: i18n.t('screens.incident.errors.button'),
          onClick: () => {
            SimpleAlert.close()

            if (onFailed) {
              onFailed()
            }
          },
        },
      ],
    })
  }
  /**
   * upload is a direct call to sync with promise result
   * @returns result from create api
   */
  const upload = (incident: IncidentForm, onFailed?: (type: FailType) => void) => {
    return synchronizeIncident(createRequest(incident), (errorType) =>
      onFail(errorType, () => onFailed && onFailed(errorType))
    ).then((res) => (!!res ? Promise.resolve() : Promise.reject()))
  }

  /**
   * Sync is a silent try in background
   */
  const sync = () => {
    Promise.all(incidents.map((i) => synchronizeIncident(i)))
      .then((ids) => {
        if (ids.filter(Boolean).length > 0) {
          // sync at least 1, update current edl form

          if (form) {
            // use replacement on serialized item
            const serialized = JSON.stringify(form)

            // apply id switch
            const next = ids.reduce((acc: string, cur, idx) => {
              const initial = incidents[idx]

              if (initial && cur) {
                const regex = new RegExp(initial.id, 'g')
                return acc.replace(regex, cur)
              }

              return acc
            }, serialized)

            // update unserialized
            EDLStore.actions.setForm(JSON.parse(next))

            ids.forEach((id) => {
              // if item synchronized
              if (id) {
                // update incident list with new item
                api.incident.get(id).then(IncidentStore.actions.addIncident).catch(Logger.error)
              }
            })
          }
        }

        // set status at completed
        setStatus('UPLOADED')
      })
      .finally(() => {
        synchronizing = false
      })
  }

  React.useEffect(() => {
    if (status === 'UPLOADING') {
      sync()
    }
  }, [status])

  React.useEffect(() => {
    if (online) {
      if (incidents.length > 0 && !synchronizing) {
        synchronizing = true
        setStatus('UPLOADING')
      }
    } else {
      setStatus('PENDING')
    }
  }, [incidents.length, online])

  const utils = {
    upload,
    sync,
    reset: SyncStore.actions.reset,
    addIncident,
    removeIncident: SyncStore.actions.removeIncident,
  }

  return [status, incidents, utils, online] as const
}

export default useSyncStore
